import { useContext, useEffect, useState } from 'react'

import InformationDisplay, { ELEMENT_TYPES } from './InformationDisplay'
import LoadingSkeleton from './LoadingSkeleton'
import moment from 'moment'
import { Alert, Button, Drawer, Modal, Tag } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faBook, faCalendarAlt, faClock, faCog, faComment, faDatabase, faDollar, faFile, faPeopleArrows, faPlusCircle, faSchool, faStethoscope, faUser
} from '@fortawesome/pro-solid-svg-icons'
import { faCheckCircle, faHouseMedical, faTimesCircle } from '@fortawesome/pro-duotone-svg-icons'
import { isFunction } from 'lodash'
import { useSelector } from 'react-redux'

import { generalErrorHandler, isNonEmptyArray, isObject, requestWithPromise } from '../../utils'
import InternshipValidationButton from './ShiftsManager/InternshipValidationButton'
import { InstitutionOptions, InternshipStates } from '../../utils/constants'
import InternshipValidation from './ShiftsManager/InternshipValidation'
import ConventionHandler from './ConventionHandler'
import ScheduleBonusTable from './ScheduleBonusTable'
import History from './History'
import { Shift } from '../../utils/entities/shift'
import { getTimezoneLessMoment } from '../../utils/momentjs'
import { getInternship, getInternshipShifts } from '../../utils/api/internship'
import { getIconByName } from '../../utils/fontAwesomeHelper'
import { DEFAULT_STATE, InternshipDocumentsDrawerContext } from '../../Providers/Drawer/InternshipDocumentsDrawer'
import { selectUser } from '../../reducers/UserReducer'
import IconButton from '../antd/Buttons/IconButton'
import { isSchoolUser } from '../../utils/roles'
import { HttpMethods } from '../../utils/apiHelper'
import { GlobalContext } from '../../Providers/GlobalProvider'
import ValidationWarning, { MODIFIED_SHIFT_TIME } from './ShiftsManager/InternshipValidation/ValidationWarning'
import SectorModal from '../../Providers/Forms/SectorModal'
import SectorLink from './SectorLink'
import ActsDisplayer from './ActsDisplayer'
import { selectTranslate } from '../../reducers/LocaleReducer/LocalReducer'
import { InstitutionContext } from '../../Providers/InstitutionProvider'
import MetadataDisplayer from './MetadataDisplayer'
import { ColorsPalette } from '../../config/colors'

import '../../assets/shifts-manager.scss'

const INTERNSHIP_ACTS_TITLE = 'common.internship_acts'

const InternshipInfo = ({ internship, loading, onClose, onInternshipValidate }) => {
  const { isReadOnly } = useContext(GlobalContext)
  const { setHandler } = useContext(InternshipDocumentsDrawerContext)
  const { options } = useContext(InstitutionContext) ?? {}

  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const [localInternship, setLocalInternship] = useState(null)
  const [shifts, setShifts] = useState([])
  const [openValidationDrawer, setOpenValidationDrawer] = useState(false)
  const [localLoading, setLocalLoading] = useState(false)
  const [loadingContactPersons, setLoadingContactPersons] = useState(true)
  const [contactPersons, setContactPersons] = useState([])
  const [contactPersonsSector, setContactPersonsSector] = useState([])
  const [contactPersonsSection, setContactPersonsSection] = useState([])
  const [scheduleBonusModalVisible, setScheduleBonusModalVisible] = useState(false)

  useEffect(() => {
    if (isObject(internship)) {
      getInternshipShifts(internship, user).then(json => {
        if (json?.data) {
          setShifts(json.data)
        }
      })
      getInternship(user, internship, { contexts: ['individualInternshipRequest', 'actTypes'] }).then(({ data }) => {
        setLocalInternship(data)
        setLocalLoading(false)
        loadContactPersons(data)
      }).catch(generalErrorHandler)
    } else {
      setLocalInternship(null)
      setLoadingContactPersons(true)
      setContactPersons([])
    }
  }, [internship, user])

  const handleInternshipValidation = () => {
    const currentInternship = { ...localInternship }

    currentInternship.state = InternshipStates.SCHEDULE_VALIDATED

    setLocalInternship(currentInternship)

    if (isFunction(onInternshipValidate)) {
      onInternshipValidate(currentInternship)
    }
  }

  const handleInternshipInvalidation = newState => {
    const currentInternship = { ...localInternship }

    currentInternship.state = newState

    setLocalInternship(currentInternship)

    if (isFunction(onInternshipValidate)) {
      onInternshipValidate(currentInternship)
    }
  }

  const renderScheduleBody = (shift, pauseIcon) => {
    if (shift.isAbsent()) {
      return <span className='absent-schedule'><b> {t('event.code.label.' + shift.eventCodeType.type)} </b></span>
    } else if (shift.isModified) {
      return <span className='modified-schedule'><b style={{ color: 'orange' }}>{shift.getSchedule()}</b> <ValidationWarning t={t} warningType={MODIFIED_SHIFT_TIME} /></span>
    } else {
      return <span className='default-schedule'> {shift.getSchedule()} {pauseIcon && <FontAwesomeIcon icon={getIconByName(pauseIcon)} />} </span>
    }
  }

  const renderSchedule = i => {
    const sortedShifts = shifts.sort((a, b) => {
      return moment(a.startDate).diff(moment(b.startDate))
    })

    return (
      <History
        data={sortedShifts.map(s => {
          const shift = new Shift(s)
          const pauseIcon = shift.getPauseIcon()

          return {
            date: shift.startDate,
            body: renderScheduleBody(shift, pauseIcon)
          }
        })}
        period={{
          startDate: getTimezoneLessMoment(i.startDate),
          endDate: getTimezoneLessMoment(i.endDate)
        }}
        strictMode
      />
    )
  }

  const loadContactPersons = async (internship) => {
    const { institution, school, sector, section } = internship

    if (institution && sector && section && school) {
      try {
        const contactPersonsUrl = isSchoolUser(user) ? `/api/institutions/${institution.id}/contact-persons` : `/api/schools/${school.id}/contact-persons`

        const { data } = await requestWithPromise(contactPersonsUrl, HttpMethods.GET, null, user)
        const dataSector = await requestWithPromise(`/api/sectors/${sector.id}/contact-persons`, HttpMethods.GET, null, user)
        const dataSection = await requestWithPromise(`/api/school-sections/${section.id}/contact-persons`, HttpMethods.GET, null, user)

        if (!data.status || data.status !== 'error') {
          setContactPersons(data)
          setContactPersonsSector(dataSector.data)
          setContactPersonsSection(dataSection.data)
        } else {
          setContactPersons([])
          setContactPersonsSector([])
          setContactPersonsSection([])
        }
      } catch (err) {
        generalErrorHandler(err)
      }
    }

    setLoadingContactPersons(false)
  }

  const renderContactPersons = () => {
    if (loadingContactPersons) {
      return (
        <div className='flex-column'>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div className='loading-icon black' />
            <div style={{ marginLeft: '4px', marginTop: '4px' }}>{t('Loading contact persons')}</div>
          </div>
        </div>
      )
    }

    if (!contactPersons.length && !contactPersonsSector.length && !contactPersonsSection.length) {
      return (
        <div className='flex-column'>
          {t('None')}
        </div>
      )
    }

    const schoolContactPersons = contactPersons.filter(item => item.type === 'school').concat(contactPersonsSection)
    const institutionContactPersons = contactPersons.filter(item => item.type === 'institution').concat(contactPersonsSector)

    return (
      <div className='flex-column'>
        {schoolContactPersons.length > 0 && schoolContactPersons.map(schoolContactPerson => (
          <div key={schoolContactPerson.email}>
            <b>{t('common.school')}:</b>&nbsp;
            {`${schoolContactPerson.lastname} ${schoolContactPerson.firstname} - `}
            <a href={'mailto:' + schoolContactPerson.email}>{schoolContactPerson.email}</a>
            {schoolContactPerson.phone !== null && schoolContactPerson.phone !== '' ? ` - ${schoolContactPerson.phone}` : ''}
          </div>
        ))}
        {institutionContactPersons.length > 0 && institutionContactPersons.map(institutionContactPerson => (
          <div key={institutionContactPerson.email}>
            <b>{t('common.institution')}:</b>&nbsp;
            {`${institutionContactPerson.lastname} ${institutionContactPerson.firstname} - `}
            <a href={'mailto:' + institutionContactPerson.email}>{institutionContactPerson.email}</a>
            {institutionContactPerson.phone !== null && institutionContactPerson.phone !== '' ? ` - ${institutionContactPerson.phone}` : ''}
          </div>
        ))}
      </div>
    )
  }

  const renderBody = () => {
    let title = ''
    let content = <LoadingSkeleton />

    if (localInternship !== null) {
      let validateButtonTooltip = t(`internship_infos.validate_button.${isReadOnly ? 'see' : 'validate'}`)

      switch (localInternship.state) {
        case InternshipStates.SCHEDULE_TO_VALIDATE:
          validateButtonTooltip = t('internship_infos.validate_button.to_validate')
          break
        case InternshipStates.SCHEDULE_VALIDATED:
          validateButtonTooltip = t('internship_infos.validate_button.validated')
          break
      }

      title = (
        <div className='internship-sheet-title'>
          <span>
            {t('Internship sheet')}
            {localLoading || loading
              ? (
                <div className='loading-icon black' style={{ marginTop: '1px' }} />
              )
              : null}
          </span>
          <span className='internship-validation-button-custom'>
            <InternshipValidationButton
              validateButtonTooltip={validateButtonTooltip}
              onDisplayValidationDrawer={() => setOpenValidationDrawer(true)}
              internship={localInternship}
            />
          </span>
        </div>
      )

      const information = [
        {
          icon: faUser,
          title: 'common.student',
          body: i => i.student.firstname + ' ' + i.student.lastname,
          isValid: i => isObject(i.student)
        },
        {
          icon: faHouseMedical,
          title: 'common.institution',
          body: i => i.institution.name,
          isValid: i => isObject(i.institution)
        },
        {
          icon: faSchool,
          title: 'common.school',
          body: i => i.school.name,
          isValid: i => isObject(i.school)
        },
        {
          icon: faStethoscope,
          title: 'common.care_unit',
          body: i => (
            <SectorModal readOnly>
              <SectorLink sector={i.sector} />
            </SectorModal>
          ),
          isValid: i => isObject(i.sector) && i.sector.parent !== null
        },
        {
          icon: faPeopleArrows,
          title: 'common.contact_persons',
          body: renderContactPersons
        },
        {
          icon: faCog,
          title: 'common.internship_year',
          body: i => i.internshipYear.schoolYearName ?? i.internshipYear.name,
          isValid: i => isObject(i.internshipYear)
        },
        {
          icon: faCalendarAlt,
          title: 'common.dates',
          body: (i, t) =>
            t('From') +
            ' ' +
            getTimezoneLessMoment(i.startDate).format('DD MMMM YYYY') +
            ' ' +
            t('to') +
            ' ' +
            getTimezoneLessMoment(i.endDate).format('DD MMMM YYYY'),
          isValid: i => i.startDate && i.endDate
        },
        {
          icon: faBook,
          title: INTERNSHIP_ACTS_TITLE,
          body: i => <ActsDisplayer actTypes={i.actTypes ?? []} />,
          isVisible: i => isNonEmptyArray(i.actTypes)
        },
        {
          icon: faClock,
          title: 'common.internship_duration',
          body: i => (
            <b>
              {' '}
              {i.duration} {t('common.hours.tlc')}{' '}
            </b>
          ),
          isVisible: i => !!i.duration
        },
        {
          icon: faDollar,
          title: 'common.internship_compensation',
          body: i => (
            <Tag color={i.compensation ? 'green' : 'red'}>
              <div className='flex-center'>
                {' '}
                {i.compensation ? t('Yes') : t('No')}{' '}
              </div>
            </Tag>
          ),
          isVisible: i =>
            isObject(options) &&
            options[InstitutionOptions.INTERNSHIP_COMPENSATION]
        },
        {
          icon: faDatabase,
          padding: '16px',
          type: ELEMENT_TYPES.PICABOO,
          title: 'common.additional_informations',
          body: i => <MetadataDisplayer metadata={i.metadata} />,
          isVisible: i => isObject(i.metadata)
        },
        {
          icon: faFile,
          title: 'common.internship_convention',
          body: i => <ConventionHandler internship={i} />
        },
        {
          icon: faFile,
          title: 'common.internship_documents',
          body: (i, t) => (
            <div>
              {isObject(options) && options[InstitutionOptions.INTERNSHIP_READING_DOCUMENTS_REQUIRED] && (
                <Alert
                  type={i.hasAcknowledgedDocuments ? 'success' : 'error'}
                  className='flex-row'
                  style={{ alignItems: 'center', marginBottom: '8px', borderRadius: '10px', width: 'fit-content' }}
                  message={
                    <div className='flex-row' style={{ fontSize: '13px', alignItems: 'center', color: ColorsPalette.GREY }}>
                      {t('common.student_certifies_documents_read')} : &nbsp;
                      <FontAwesomeIcon
                        icon={i.hasAcknowledgedDocuments ? faCheckCircle : faTimesCircle}
                        style={{ color: i.hasAcknowledgedDocuments ? 'green' : 'red' }}
                      />
                    </div>
                  }
                />
              )}
              <Button
                type='default'
                size='small'
                onClick={() => setHandler({ ...DEFAULT_STATE, data: i })}
              >
                <FontAwesomeIcon icon='paperclip' />
                &nbsp;{t('common.attached_files')}
              </Button>
            </div>
          )
        },
        {
          icon: faCalendarAlt,
          style: { width: '100%' },
          title: (_, t) => (
            <div className='schedule-title full-width flex-row'>
              <b> {t('common.schedule')} </b>
              {!isReadOnly && isSchoolUser(user) && (
                <IconButton
                  icon={faPlusCircle}
                  color='blue'
                  size='small'
                  text={t('Hour bonus')}
                  onClick={() => setScheduleBonusModalVisible(true)}
                />
              )}
            </div>
          ),
          body: renderSchedule
        }
      ]

      if (localInternship?.individualInternshipRequest?.comment?.length > 0) {
        information.push({
          icon: faComment,
          title: 'common.comment',
          body: () => localInternship?.individualInternshipRequest?.comment
        })
      }

      content = (
        <div className='flex-column'>
          <InformationDisplay information={information} entity={localInternship} />
        </div>
      )
    }

    return (
      <Drawer
        title={title}
        width='550px'
        onClose={onClose}
        visible={internship !== null}
      >
        {content}
        {openValidationDrawer &&
          <InternshipValidation
            internship={localInternship}
            onClose={() => setOpenValidationDrawer(false)}
            onValidate={handleInternshipValidation}
            onInvalidate={handleInternshipInvalidation}
          />}
        {isSchoolUser(user) && (
          <Modal
            title={t('Hour bonus')}
            visible={scheduleBonusModalVisible}
            onCancel={() => setScheduleBonusModalVisible(false)}
            footer={null}
            destroyOnClose
            width={750}
          >
            <ScheduleBonusTable internship={localInternship} />
          </Modal>
        )}
      </Drawer>
    )
  }

  return (renderBody())
}

export default InternshipInfo
